import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Ruler, Cpu, Plus, Wifi, ClipboardList } from 'lucide-react';
import { VUKZ } from '../../theme/vukzTheme';
import { AZURE } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useActiveContext } from '../../contexts/ActiveContextContext';
import { useRBAC } from '../../contexts/RBACContext';
import MeasurementsPanel from '../../components/athlete/MeasurementsPanel';
import PageLayout from '../../components/layout/PageLayout';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';
import { FEATURES } from '../../config/features';

const PROFILE_LABELS = {
  pt: {
    tabMeasurements: 'Medidas', tabSensors: 'Sensores',
    currentMeasurements: 'Medidas Atuais', physicalTests: 'Testes Físicos',
    measureHistory: 'Histórico de Medidas', weightEvolution: 'Evolução do Peso (kg)',
    metrics: {
      height: 'Altura', weight: 'Peso', wingspan: 'Envergadura', bodyFat: '% Gordura',
      leanMass: 'Massa Magra', muscleMass: 'Massa Muscular', bmi: 'IMC',
      boneMass: 'Massa Óssea', restingHr: 'FC Repouso', vo2max: 'VO₂ Máx',
      flexibility: 'Flexibilidade', grip: 'Preenção',
    },
    tableDate: 'Data', tableHeight: 'Altura', tableWeight: 'Peso', tableFat: '% Gord.',
    tableLean: 'Massa Magra', tableBmi: 'IMC', tableStaff: 'Resp.',
    noMeasurements: 'Nenhuma medida registrada.', noMeasurementsHint: 'Solicite ao preparador físico uma avaliação.',
    sensorsTitle: 'Meus Sensores', sensorsSubtitle: 'Dispositivos cadastrados para coleta de dados',
    addSensor: 'Adicionar', sensorType: 'Tipo', sensorBrand: 'Marca', sensorModel: 'Modelo',
    sensorPlacement: 'Posição no corpo', sensorPlaceholderPlacement: 'Ex: pulso esquerdo',
    cancelSensor: 'Cancelar', saveSensor: 'Salvar Sensor', savingSensor: 'Salvando...',
    noSensors: 'Nenhum sensor cadastrado', noSensorsHint: 'Adicione seus dispositivos para integrar dados de treino.',
    loadingSensors: 'Carregando sensores...',
    deviceTypes: [
      { value: 'IMU', label: 'IMU', desc: 'Unidade inercial (acelerômetro + giroscópio)' },
      { value: 'HR', label: 'Frequência Cardíaca', desc: 'Monitor cardíaco' },
      { value: 'GPS', label: 'GPS', desc: 'Rastreamento de posição' },
      { value: 'CAMERA', label: 'Câmera', desc: 'Análise de vídeo' },
      { value: 'SMARTWATCH', label: 'Smartwatch', desc: 'Relógio inteligente' },
      { value: 'STROKE_COUNTER', label: 'Conta-braçadas', desc: 'Sensor de braçadas' },
      { value: 'PRESSURE', label: 'Pressão', desc: 'Sensor de pressão/força' },
      { value: 'OTHER', label: 'Outro', desc: 'Sensor customizado' },
    ],
    selfProfile: 'Meu Perfil', profileSubtitle: 'Perfil do Atleta', loading: 'Carregando...',
    tabRegistration: 'Cadastro',
    loadError: 'Não foi possível carregar a ficha do atleta.',
    retry: 'Tentar novamente',
  },
  en: {
    tabMeasurements: 'Measurements', tabSensors: 'Sensors',
    currentMeasurements: 'Current Measurements', physicalTests: 'Physical Tests',
    measureHistory: 'Measurement History', weightEvolution: 'Weight Evolution (kg)',
    metrics: {
      height: 'Height', weight: 'Weight', wingspan: 'Wingspan', bodyFat: '% Body Fat',
      leanMass: 'Lean Mass', muscleMass: 'Muscle Mass', bmi: 'BMI',
      boneMass: 'Bone Mass', restingHr: 'Resting HR', vo2max: 'VO₂ Max',
      flexibility: 'Flexibility', grip: 'Grip Strength',
    },
    tableDate: 'Date', tableHeight: 'Height', tableWeight: 'Weight', tableFat: '% Fat',
    tableLean: 'Lean Mass', tableBmi: 'BMI', tableStaff: 'By',
    noMeasurements: 'No measurements recorded.', noMeasurementsHint: 'Ask the S&C coach for an evaluation.',
    sensorsTitle: 'My Sensors', sensorsSubtitle: 'Registered devices for data collection',
    addSensor: 'Add', sensorType: 'Type', sensorBrand: 'Brand', sensorModel: 'Model',
    sensorPlacement: 'Body placement', sensorPlaceholderPlacement: 'e.g. left wrist',
    cancelSensor: 'Cancel', saveSensor: 'Save Sensor', savingSensor: 'Saving...',
    noSensors: 'No sensors registered', noSensorsHint: 'Add your devices to integrate training data.',
    loadingSensors: 'Loading sensors...',
    deviceTypes: [
      { value: 'IMU', label: 'IMU', desc: 'Inertial unit (accelerometer + gyroscope)' },
      { value: 'HR', label: 'Heart Rate', desc: 'Heart rate monitor' },
      { value: 'GPS', label: 'GPS', desc: 'Position tracking' },
      { value: 'CAMERA', label: 'Camera', desc: 'Video analysis' },
      { value: 'SMARTWATCH', label: 'Smartwatch', desc: 'Smart watch' },
      { value: 'STROKE_COUNTER', label: 'Stroke Counter', desc: 'Stroke sensor' },
      { value: 'PRESSURE', label: 'Pressure', desc: 'Pressure/force sensor' },
      { value: 'OTHER', label: 'Other', desc: 'Custom sensor' },
    ],
    selfProfile: 'My Profile', profileSubtitle: 'Athlete Profile', loading: 'Loading...',
    tabRegistration: 'Registration',
    loadError: 'Could not load the athlete profile.',
    retry: 'Retry',
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
// (formatTime/formatDate/Skeleton foram junto com a MeasurementsTab para o
// MeasurementsPanel — aqui só sobrou o TabButton)

const TabButton = ({ active, label, icon: Icon, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
      active ? 'text-white' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
    }`}
    style={active ? { backgroundColor: VUKZ.primary } : {}}
  >
    <Icon size={14} /> {label}
  </button>
);

// ─── Measurements Tab ─────────────────────────────────────────────────────────
// EXTRAÍDA para components/athlete/MeasurementsPanel.jsx (07/08/2026) — fonte
// única compartilhada com a CoachAthleteDetailPage (aba "Físico"). Labels de
// medidas agora vivem no painel.

// ─── Sensors Tab ──────────────────────────────────────────────────────────────

const SensorsTab = ({ athleteId, clubId, t }) => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ device_type: 'IMU', brand: '', model: '', serial_number: '', placement: '' });
  const DEVICE_TYPES = t.deviceTypes;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await AZURE.get('/vukz/sensor/devices', { params: { institution_id: clubId } });
        // Filter devices for this athlete (or show all club devices)
        setDevices(res.data?.data || []);
      } catch (e) {
        console.error('Load devices error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [clubId]);

  const handleAdd = async () => {
    if (!form.device_type) return;
    setSaving(true);
    try {
      await AZURE.post('/vukz/sensor/devices', {
        institution_id: clubId,
        device_type: form.device_type,
        brand: form.brand || null,
        model: form.model || null,
        serial_number: form.serial_number || null,
        placement: form.placement || null,
      });
      // Reload
      const res = await AZURE.get('/vukz/sensor/devices', { params: { institution_id: clubId } });
      setDevices(res.data?.data || []);
      setForm({ device_type: 'IMU', brand: '', model: '', serial_number: '', placement: '' });
      setShowForm(false);
    } catch (e) {
      console.error('Add device error:', e);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: VUKZ.textLight }}>{t.loadingSensors}</div>;

  return (
    <div>
      {/* Header + Add button */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: VUKZ.text, margin: 0 }}>{t.sensorsTitle}</h2>
          <p style={{ fontSize: 12, color: VUKZ.textLight, margin: 0 }}>{t.sensorsSubtitle}</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: VUKZ.text, color: '#fff', fontSize: 13, fontWeight: 600,
          }}
        >
          <Plus size={14} /> {t.addSensor}
        </button>
      </div>

      {/* Add form */}
      {showForm && (
        <div style={{ background: '#fff', border: `1px solid ${VUKZ.border}`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: VUKZ.textLight, display: 'block', marginBottom: 4 }}>{t.sensorType}</label>
              <select value={form.device_type} onChange={e => setForm(f => ({ ...f, device_type: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${VUKZ.border}`, fontSize: 13 }}>
                {DEVICE_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: VUKZ.textLight, display: 'block', marginBottom: 4 }}>{t.sensorBrand}</label>
              <input value={form.brand} onChange={e => setForm(f => ({ ...f, brand: e.target.value }))} placeholder="Ex: Garmin"
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${VUKZ.border}`, fontSize: 13 }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: VUKZ.textLight, display: 'block', marginBottom: 4 }}>{t.sensorModel}</label>
              <input value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value }))} placeholder="Ex: Forerunner 955"
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${VUKZ.border}`, fontSize: 13 }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: VUKZ.textLight, display: 'block', marginBottom: 4 }}>{t.sensorPlacement}</label>
              <input value={form.placement} onChange={e => setForm(f => ({ ...f, placement: e.target.value }))} placeholder={t.sensorPlaceholderPlacement}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${VUKZ.border}`, fontSize: 13 }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
            <button onClick={() => setShowForm(false)}
              style={{ padding: '7px 14px', borderRadius: 8, border: `1px solid ${VUKZ.border}`, background: '#fff', cursor: 'pointer', fontSize: 13 }}>
              {t.cancelSensor}
            </button>
            <button onClick={handleAdd} disabled={saving}
              style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: VUKZ.text, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: saving ? 0.6 : 1 }}>
              {saving ? t.savingSensor : t.saveSensor}
            </button>
          </div>
        </div>
      )}

      {/* Device list */}
      {devices.length === 0 ? (
        <div style={{ background: '#fff', border: `1px solid ${VUKZ.border}`, borderRadius: 12, padding: 40, textAlign: 'center' }}>
          <Wifi size={32} style={{ color: VUKZ.border, margin: '0 auto 12px' }} />
          <p style={{ fontSize: 14, fontWeight: 600, color: VUKZ.text }}>{t.noSensors}</p>
          <p style={{ fontSize: 12, color: VUKZ.textLight }}>{t.noSensorsHint}</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {devices.map(dev => {
            const typeInfo = DEVICE_TYPES.find(d => d.value === dev.device_type) || { label: dev.device_type, desc: '' };
            return (
              <div key={dev.device_id} style={{ background: '#fff', border: `1px solid ${VUKZ.border}`, borderRadius: 10, padding: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: VUKZ.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Cpu size={18} style={{ color: VUKZ.primary }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: VUKZ.text }}>{typeInfo.label}{dev.brand ? ` — ${dev.brand}` : ''}</div>
                  <div style={{ fontSize: 12, color: VUKZ.textLight }}>
                    {dev.model || typeInfo.desc}{dev.placement ? ` · ${dev.placement}` : ''}
                  </div>
                </div>
                <div style={{ fontSize: 10, color: VUKZ.textLight, whiteSpace: 'nowrap' }}>
                  {dev.serial_number || ''}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Athlete Profile Page — Tabbed (Results + Measurements + Sensors)
// ═══════════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════════
// INV-5B — InviteCard do atleta (GESTOR, fora do self mode).
// Mesma disciplina do Staff: token é bearer credential, só existe em memória
// React após o POST /roster/athletes/:id/invite (única rota que o devolve);
// o read model (/full → pending_invite) NUNCA o expõe.
// account_state vem do BACKEND — zero heurística de user_id/datas aqui.
// ═══════════════════════════════════════════════════════════════════════════════
const AthleteInviteCard = ({ athleteId, athlete, onGoEdit, onChanged }) => {
  const [sending, setSending] = useState(false);
  const [inviteErr, setInviteErr] = useState(null);
  const [delivery, setDelivery] = useState(null); // {url, reused, expiresAt, email}
  const [copied, setCopied] = useState(false);

  const state = athlete?.account_state;
  const pending = athlete?.pending_invite || null;
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('pt-BR') : null;

  const sendInvite = async () => {
    setSending(true); setInviteErr(null); setCopied(false);
    try {
      const { data } = await AZURE.post(`/roster/athletes/${athleteId}/invite`, {});
      const token = data?.invite?.invite_token;
      if (!token) throw new Error('Resposta sem token');
      setDelivery({
        url: `${window.location.origin}/accept-invite?token=${encodeURIComponent(token)}`,
        reused: !!data.invite.reused,
        expiresAt: data.invite.expires_at,
        email: data.invite.invite_email,
      });
      // NÃO recarrega a ficha: load() remontaria o card e perderíamos o
      // delivery (token só existe nesta resposta).
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.error;
      if (status === 409 && /conta/i.test(msg || '')) {
        setInviteErr(msg);
        await onChanged();
      } else if (status === 422) {
        setInviteErr(msg || 'O atleta precisa ter e-mail cadastrado. Edite os dados na aba Cadastro.');
      } else {
        setInviteErr(msg || 'Erro ao enviar o convite. Tente novamente.');
      }
    } finally { setSending(false); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(delivery.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setInviteErr('Não foi possível copiar. Selecione o link manualmente.');
    }
  };

  const waHref = delivery ? `https://wa.me/?text=${encodeURIComponent(
    `Olá${athlete.full_name ? `, ${athlete.full_name}` : ''}! Você foi convidado(a) `
    + `para acessar a VUKZ AI. Crie sua conta ou entre pelo link: `
    + delivery.url)}` : null;

  return (
    <div className="bg-white border rounded-xl p-4 mb-4" style={{ borderColor: VUKZ.border }}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-bold" style={{ color: VUKZ.text }}>
            Acesso à VUKZ AI
          </div>
          {state === 'ACCOUNT_ACTIVE' && (
            <p className="text-sm mt-1" style={{ color: '#047857' }}>
              ✓ Conta VUKZ AI ativa.
            </p>
          )}
          {state === 'INVITE_PENDING' && !delivery && (
            <p className="text-sm mt-1" style={{ color: VUKZ.textLight }}>
              Convite pendente para <strong style={{ color: VUKZ.text }}>{pending?.invite_email}</strong>
              {pending?.expires_at && <> · válido até {fmtDate(pending.expires_at)}</>}.
            </p>
          )}
          {state === 'NO_ACCOUNT' && !delivery && (
            athlete?.email ? (
              <p className="text-sm mt-1" style={{ color: VUKZ.textLight }}>
                Este atleta ainda não tem acesso à plataforma.
              </p>
            ) : (
              <p className="text-sm mt-1" style={{ color: '#854d0e' }}>
                Cadastre um e-mail para enviar acesso.
              </p>
            )
          )}
        </div>
        {state !== 'ACCOUNT_ACTIVE' && (
          athlete?.email ? (
            <button onClick={sendInvite} disabled={sending}
              className="px-3 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-60"
              style={{ backgroundColor: VUKZ.text }}>
              {sending ? 'Enviando…'
                : (state === 'INVITE_PENDING' || delivery) ? 'Reenviar convite' : 'Enviar convite'}
            </button>
          ) : (
            <button onClick={onGoEdit}
              className="px-3 py-2 rounded-lg text-xs font-bold border"
              style={{ borderColor: VUKZ.border, color: VUKZ.text }}>
              Editar cadastro
            </button>
          )
        )}
      </div>

      {inviteErr && (
        <div className="mt-3 text-sm rounded-lg border px-3 py-2"
          style={{ borderColor: '#fca5a5', color: '#b91c1c', backgroundColor: '#fef2f2' }}>
          {inviteErr}
        </div>
      )}

      {delivery && (
        <div className="mt-3 border rounded-lg p-3" style={{ borderColor: VUKZ.border }}>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="px-1.5 py-0.5 rounded border text-[10px] font-semibold"
              style={delivery.reused
                ? { borderColor: VUKZ.border, color: VUKZ.textLight }
                : { borderColor: '#a7f3d0', color: '#047857', backgroundColor: '#f0fdf4' }}>
              {delivery.reused ? 'convite reaproveitado' : 'novo convite'}
            </span>
            {delivery.expiresAt && (
              <span className="text-[11px]" style={{ color: VUKZ.textLight }}>
                válido até {fmtDate(delivery.expiresAt)}
              </span>
            )}
          </div>
          <code className="block text-xs break-all px-2 py-1.5 rounded border mb-2"
            style={{ borderColor: VUKZ.border, color: VUKZ.text, backgroundColor: '#f8fafc' }}>
            {delivery.url}
          </code>
          <div className="flex gap-2">
            <button onClick={copyLink}
              className="px-3 py-1.5 rounded-lg text-xs font-bold text-white"
              style={{ backgroundColor: copied ? '#047857' : VUKZ.text }}>
              {copied ? 'Copiado!' : 'Copiar link'}
            </button>
            <a href={waHref} target="_blank" rel="noopener noreferrer"
              className="px-3 py-1.5 rounded-lg text-xs font-bold border"
              style={{ borderColor: VUKZ.border, color: VUKZ.text }}>
              WhatsApp
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// INV-5B — Aba Cadastro (GESTOR, fora do self mode).
// Campos que o PUT real já persiste (não inventar campos novos).
// Semântica registrada: campo vazio NÃO apaga (SP usa COALESCE) — enviamos
// null para "não alterar" e bloqueamos submit sem nome.
// ═══════════════════════════════════════════════════════════════════════════════
const RegistrationTab = ({ athleteId, athlete, onGoEditRef, onChanged }) => {
  const [form, setForm] = useState({
    full_name: athlete?.full_name || '',
    email: athlete?.email || '',
    birth_date: athlete?.birth_date ? String(athlete.birth_date).slice(0, 10) : '',
    gender: athlete?.gender || '',
  });
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState(null);
  const [saved, setSaved] = useState(false);

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form.full_name.trim()) return;
    setSaving(true); setSaveErr(null); setSaved(false);
    try {
      await AZURE.put(`/roster/athletes/${athleteId}`, {
        full_name: form.full_name.trim(),
        email: form.email.trim() || null,       // null = não alterar (COALESCE)
        birth_date: form.birth_date || null,
        gender: form.gender || null,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      await onChanged(); // reload da ficha — estado do InviteCard reflete e-mail novo
    } catch (err) {
      setSaveErr(err?.response?.data?.error || 'Erro ao salvar. Tente novamente.');
    } finally { setSaving(false); }
  };

  const field = (label, key, type = 'text') => (
    <div>
      <label className="text-xs font-bold block mb-1" style={{ color: VUKZ.textLight }}>{label}</label>
      <input type={type} value={form[key]}
        onChange={(e) => setForm(f => ({ ...f, [key]: e.target.value }))}
        className="w-full px-3 py-2.5 rounded-xl border text-sm" style={{ borderColor: VUKZ.border }} />
    </div>
  );

  return (
    <div>
      <AthleteInviteCard athleteId={athleteId} athlete={athlete}
        onGoEdit={onGoEditRef} onChanged={onChanged} />
      <form onSubmit={save} className="bg-white border rounded-xl p-4 space-y-4"
        style={{ borderColor: VUKZ.border }}>
        <div className="text-sm font-bold" style={{ color: VUKZ.text }}>Dados cadastrais</div>
        {field('Nome completo *', 'full_name')}
        {field('E-mail', 'email', 'email')}
        <div className="grid grid-cols-2 gap-3">
          {field('Data de nascimento', 'birth_date', 'date')}
          <div>
            <label className="text-xs font-bold block mb-1" style={{ color: VUKZ.textLight }}>Gênero</label>
            <select value={form.gender}
              onChange={(e) => setForm(f => ({ ...f, gender: e.target.value }))}
              className="w-full px-3 py-2.5 rounded-xl border text-sm" style={{ borderColor: VUKZ.border }}>
              <option value="">—</option>
              <option value="male">Masculino</option>
              <option value="female">Feminino</option>
            </select>
          </div>
        </div>
        {saveErr && (
          <div className="text-sm rounded-lg border px-3 py-2"
            style={{ borderColor: '#fca5a5', color: '#b91c1c', backgroundColor: '#fef2f2' }}>
            {saveErr}
          </div>
        )}
        <button type="submit" disabled={saving || !form.full_name.trim()}
          className="px-4 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50"
          style={{ backgroundColor: saved ? '#047857' : VUKZ.text }}>
          {saving ? 'Salvando…' : saved ? '✓ Salvo' : 'Salvar'}
        </button>
      </form>
    </div>
  );
};

export default function AthleteProfilePage() {
  const { athleteId } = useParams();
  const { user } = useAuth();
  const { clubId } = useActiveContext();
  const { roles } = useRBAC();
  const { lang } = useGovernanceLang();
  const t = PROFILE_LABELS[lang] || PROFILE_LABELS.pt;
  const [tab, setTab] = useState('measurements');
  const [athlete, setAthlete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);

  // If no athleteId, this is "Meu Perfil" (self)
  const isSelf = !athleteId;
  const resolvedId = athleteId || user?.id;
  // INV-5B: administração (Cadastro + convite) só p/ GESTOR fora do self mode.
  // TREINADOR/PREPARADOR têm acesso operacional (medidas), não administrativo.
  const isAdmin = !isSelf && (roles || []).includes('GESTOR');

  const loadAthlete = useCallback(async () => {
    setLoading(true); setLoadErr(null);
    try {
      if (isSelf) {
        // Self profile — use user data
        setAthlete({ full_name: user?.full_name || user?.name || 'Meu Perfil', user_id: user?.id });
      } else {
        // INV-5B: /full é o read model canônico (GET /roster/athletes/:id
        // NÃO existe). Sem fallback silencioso — erro de carga é erro visível.
        const res = await AZURE.get(`/roster/athletes/${athleteId}/full`);
        setAthlete(res.data?.data || null);
      }
    } catch (e) {
      console.error('Load athlete error:', e);
      setAthlete(null);
      setLoadErr(t.loadError);
    } finally {
      setLoading(false);
    }
  }, [athleteId, isSelf, user, t.loadError]);

  useEffect(() => { loadAthlete(); }, [loadAthlete]);

  const pageTitle = loading ? t.loading : (isSelf ? t.selfProfile : athlete?.full_name || '—');
  const pageSubtitle = athlete?.category_name || t.profileSubtitle;

  return (
    <PageLayout header={{
      title: pageTitle,
      subtitle: pageSubtitle,
      secondaryContent: (
        <div className="flex gap-1 mt-2">
          {isAdmin && (
            <TabButton active={tab === 'registration'} label={t.tabRegistration} icon={ClipboardList} onClick={() => setTab('registration')} />
          )}
          <TabButton active={tab === 'measurements'} label={t.tabMeasurements} icon={Ruler} onClick={() => setTab('measurements')} />
          {/* Tab Sensores gated por FEATURES.ENABLE_VUKZ_SENSOR_UI (sensor fora do MVP, fail-closed default false) */}
          {FEATURES.ENABLE_VUKZ_SENSOR_UI && (
            <TabButton active={tab === 'sensors'} label={t.tabSensors} icon={Cpu} onClick={() => setTab('sensors')} />
          )}
        </div>
      )
    }}>
      {loadErr && !loading && (
        <div className="bg-white border rounded-xl p-4 mb-4 flex items-center justify-between"
          style={{ borderColor: '#fca5a5' }}>
          <span className="text-sm" style={{ color: '#b91c1c' }}>{loadErr}</span>
          <button onClick={loadAthlete}
            className="px-3 py-1.5 border rounded-lg text-xs font-bold"
            style={{ borderColor: VUKZ.border, color: VUKZ.text }}>
            {t.retry}
          </button>
        </div>
      )}
      {isAdmin && tab === 'registration' && !loading && athlete && (
        <RegistrationTab key={athlete.email || 'reg'} athleteId={athleteId}
          athlete={athlete} onGoEditRef={() => setTab('registration')}
          onChanged={loadAthlete} />
      )}
      {tab === 'measurements' && <MeasurementsPanel athleteId={resolvedId} clubId={clubId} gender={athlete?.gender} lang={lang} />}
      {/* SensorsTab gated por FEATURES.ENABLE_VUKZ_SENSOR_UI — defesa em profundidade caso state esteja em 'sensors' */}
      {FEATURES.ENABLE_VUKZ_SENSOR_UI && tab === 'sensors' && <SensorsTab athleteId={resolvedId} clubId={clubId} t={t} />}
    </PageLayout>
  );
}
